import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Mail } from "lucide-react";
import LanguageSwitcher from "../../i18n/LanguageSwitcher";
import QenvaWordmark from "../../components/brand/QenvaWordmark";

// Brand palette from the Brand Kit — same as Home.jsx to keep the marketing
// surface visually consistent.
const BRAND = {
  navy:  "#0B2341",
  blue:  "#1677FF",
  slate: "#64748B",
  off:   "#F5F7FA",
};

/**
 * Shared shell for /legal/* placeholder pages (Terms + Privacy).
 * Public — no auth required. Deep-Navy header with QENVA logo, Inter body,
 * a clean centered content column, and a "back home" affordance.
 *
 * Iter76 — placeholder text only. Full T&Cs and Privacy Policy will be
 * published before commercial launch. i18n scope: `legal`.
 */
export default function LegalLayout({
  metaTitle,
  kicker,
  pageTitle,
  body,
  backHomeLabel,
  contactLabel,
  contactEmail,
  testId,
}) {
  useEffect(() => {
    if (metaTitle) document.title = metaTitle;
  }, [metaTitle]);

  const { t } = useTranslation("legal");
  return (
    <div
      className="min-h-screen flex flex-col"
      style={{
        background: BRAND.off,
        color: BRAND.navy,
        fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
      }}
      data-testid={testId}
    >
      {/* Header — matches Home.jsx style */}
      <header
        className="border-b"
        style={{ background: "white", borderColor: "rgba(11,35,65,0.08)" }}
      >
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="legal-logo-link">
            <QenvaWordmark tone="light" size={30} testId="legal-logo-wordmark" />
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSwitcher variant="compact" testIdPrefix="legal-lang" />
            <Link
              to="/"
              className="hidden sm:inline-flex items-center gap-1.5 text-sm text-zinc-700 hover:text-zinc-950 transition-colors"
              data-testid="legal-back-home"
            >
              <ArrowLeft size={14} /> {backHomeLabel}
            </Link>
          </div>
        </div>
      </header>

      {/* Main content column */}
      <main className="flex-1">
        <article className="max-w-3xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
          <p
            className="text-[11px] font-semibold tracking-[0.18em] uppercase mb-3"
            style={{ color: BRAND.blue }}
            data-testid="legal-kicker"
          >
            {kicker}
          </p>
          <h1
            className="font-semibold tracking-tight text-3xl sm:text-5xl leading-[1.1]"
            style={{
              color: BRAND.navy,
              fontFamily: "'Bodoni Moda', 'Playfair Display', Georgia, serif",
              fontWeight: 500,
              letterSpacing: "0.02em",
            }}
            data-testid="legal-page-title"
          >
            {pageTitle}
          </h1>

          <div
            className="mt-8 rounded-2xl border bg-white p-6 sm:p-8"
            style={{ borderColor: "rgba(11,35,65,0.08)" }}
          >
            <p
              className="text-base leading-relaxed"
              style={{ color: BRAND.slate }}
              data-testid="legal-body"
            >
              {body}
            </p>
          </div>

          {/* Contact strip */}
          <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-t pt-6"
               style={{ borderColor: "rgba(11,35,65,0.08)" }}>
            <div className="text-sm" style={{ color: BRAND.slate }}>
              {contactLabel}
            </div>
            <a
              href={`mailto:${contactEmail}`}
              className="inline-flex items-center gap-1.5 text-sm font-medium hover:underline"
              style={{ color: BRAND.blue }}
              data-testid="legal-contact-email"
            >
              <Mail size={14} /> {contactEmail}
            </a>
          </div>

          <div className="mt-10 sm:hidden">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm text-zinc-700 hover:text-zinc-950 transition-colors"
              data-testid="legal-back-home-mobile"
            >
              <ArrowLeft size={14} /> {backHomeLabel}
            </Link>
          </div>
        </article>
      </main>

      {/* Minimal footer */}
      <footer
        className="py-8 border-t"
        style={{ borderColor: "rgba(11,35,65,0.08)" }}
      >
        <div className="max-w-6xl mx-auto px-5 sm:px-8 text-center">
          <p className="text-xs" style={{ color: BRAND.slate }}>
            © 2026 QENVA
          </p>
        </div>
      </footer>
    </div>
  );
}
